import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ListEnvelope } from '../../../shared/api/genexus-query';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import {
  createCompanyNotasFiscaisApi,
  listAllCompanyNotasFiscais,
  type CompanyNotasFiscaisApi,
} from './api';
import type {
  CompanyNotaFiscalCancelarInput,
  CompanyNotaFiscalDto,
  CompanyNotaFiscalEvento,
  CompanyNotaFiscalGerarAutomaticaInput,
  CompanyNotaFiscalGerarManualInput,
  CompanyNotaFiscalListItem,
  CompanyNotasFiscaisListExtraParams,
  RelatorioExtratoParams,
} from './types';

interface CancelarVars {
  id: string;
  input: CompanyNotaFiscalCancelarInput;
  /** Obrigatório na lista de todas as empresas; na lista de uma emitente o hook ignora. */
  empresaId?: string;
}

interface NotaActionVars {
  id: string;
  empresaId?: string;
}

export interface CompanyNotasFiscaisHooks {
  api: CompanyNotasFiscaisApi;
  useList: (
    query: DataTableQuery,
  ) => UseQueryResult<ListEnvelope<CompanyNotaFiscalListItem>, Error>;
  useDetail: (id: string | null | undefined) => UseQueryResult<CompanyNotaFiscalDto, Error>;
  useGerarAutomatica: () => UseMutationResult<
    CompanyNotaFiscalDto,
    Error,
    CompanyNotaFiscalGerarAutomaticaInput
  >;
  useGerarManual: () => UseMutationResult<
    CompanyNotaFiscalDto,
    Error,
    CompanyNotaFiscalGerarManualInput
  >;
  useCancelar: () => UseMutationResult<CompanyNotaFiscalDto, Error, CancelarVars>;
  useUploadFile: () => UseMutationResult<
    CompanyNotaFiscalDto,
    Error,
    { id: string; file: File; empresaId?: string }
  >;
  useDownloadFile: () => UseMutationResult<Blob, Error, NotaActionVars>;
  useRelatorioExtrato: () => UseMutationResult<Blob, Error, RelatorioExtratoParams>;
  useEventos: (
    id: string | null | undefined,
    empresaId?: string,
  ) => UseQueryResult<CompanyNotaFiscalEvento[], Error>;
  useReenviar: () => UseMutationResult<CompanyNotaFiscalDto, Error, NotaActionVars>;
}

export function useCompanyNotasFiscaisHooks(
  companyId: number | null,
  extra: CompanyNotasFiscaisListExtraParams = {},
  listEnabled = true,
): CompanyNotasFiscaisHooks {
  // `extra` é um objeto literal recriado a cada render no caller; usamos a
  // forma serializada como chave estável pra evitar listar campo a campo
  // (frágil quando spec adiciona filtro novo).
  const extraKey = JSON.stringify(extra);
  const nfApi = useMemo(
    () => createCompanyNotasFiscaisApi(companyId ?? 0, extra),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId, extraKey],
  );
  const apiFor = (empresaId?: string): CompanyNotasFiscaisApi => {
    if (companyId !== null) return nfApi;
    const id = Number(empresaId);
    if (!Number.isFinite(id) || id <= 0) {
      throw new Error('Empresa da nota não informada');
    }
    return createCompanyNotasFiscaisApi(id);
  };
  const queryKey = useMemo(
    () => ['companies', companyId ?? 'all', 'notas-fiscais'] as const,
    [companyId],
  );
  const qc = useQueryClient();

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey });
  };

  const useList = (
    query: DataTableQuery,
  ): UseQueryResult<ListEnvelope<CompanyNotaFiscalListItem>, Error> =>
    useQuery({
      queryKey: [...queryKey, 'list', query, extra],
      queryFn: () =>
        companyId === null ? listAllCompanyNotasFiscais(query, extra) : nfApi.list(query),
      enabled: listEnabled,
      placeholderData: (previous) => previous,
    });

  const useDetail = (id: string | null | undefined): UseQueryResult<CompanyNotaFiscalDto, Error> =>
    useQuery({
      queryKey: [...queryKey, 'detail', id],
      queryFn: () => apiFor().get(id as string),
      enabled: id !== null && id !== undefined,
    });

  const useGerarAutomatica = (): UseMutationResult<
    CompanyNotaFiscalDto,
    Error,
    CompanyNotaFiscalGerarAutomaticaInput
  > =>
    useMutation({
      mutationFn: (input) => nfApi.gerarAutomatica(input),
      onSuccess: invalidate,
    });

  const useGerarManual = (): UseMutationResult<
    CompanyNotaFiscalDto,
    Error,
    CompanyNotaFiscalGerarManualInput
  > =>
    useMutation({
      mutationFn: (input) => nfApi.gerarManual(input),
      onSuccess: invalidate,
    });

  const useCancelar = (): UseMutationResult<CompanyNotaFiscalDto, Error, CancelarVars> =>
    useMutation({
      mutationFn: ({ id, input, empresaId }) => apiFor(empresaId).cancelar(id, input),
      onSuccess: invalidate,
    });

  const useUploadFile = (): UseMutationResult<
    CompanyNotaFiscalDto,
    Error,
    { id: string; file: File; empresaId?: string }
  > =>
    useMutation({
      mutationFn: ({ id, file, empresaId }) => apiFor(empresaId).uploadFile(id, file),
      onSuccess: invalidate,
    });

  const useDownloadFile = (): UseMutationResult<Blob, Error, NotaActionVars> =>
    useMutation({
      mutationFn: ({ id, empresaId }) => apiFor(empresaId).downloadFile(id),
    });

  const useRelatorioExtrato = (): UseMutationResult<Blob, Error, RelatorioExtratoParams> =>
    useMutation({
      mutationFn: (params) => apiFor().relatorioExtrato(params),
    });

  const useEventos = (
    id: string | null | undefined,
    empresaId?: string,
  ): UseQueryResult<CompanyNotaFiscalEvento[], Error> =>
    useQuery({
      queryKey: [...queryKey, 'eventos', id, empresaId],
      queryFn: () => apiFor(empresaId).listEventos(id as string),
      enabled: id !== null && id !== undefined,
      staleTime: 0,
    });

  const useReenviar = (): UseMutationResult<CompanyNotaFiscalDto, Error, NotaActionVars> =>
    useMutation({
      mutationFn: ({ id, empresaId }) => apiFor(empresaId).reenviar(id),
      onSuccess: invalidate,
    });

  return {
    api: nfApi,
    useList,
    useDetail,
    useGerarAutomatica,
    useGerarManual,
    useCancelar,
    useUploadFile,
    useDownloadFile,
    useRelatorioExtrato,
    useEventos,
    useReenviar,
  };
}
